import { type RefObject, useLayoutEffect, useRef } from 'react';

/** Stable attribute for the mobile contained-scroll body used by pagination scroll reset. */
export const TABLE_MOBILE_SCROLL_CONTAINER = 'table-mobile' as const;

const MOBILE_SCROLL_BODY_SELECTOR = `[data-scroll-container="${TABLE_MOBILE_SCROLL_CONTAINER}"]`;
const TABLE_CONTAINER_SCROLL_SELECTOR = '.table-container-scroll';
const MUI_TABLE_CONTAINER_SELECTOR = '.MuiTableContainer-root';

/**
 * Finds the scrollable element within a table root for pagination scroll reset.
 * Prefers the mobile contained-scroll body, then the desktop scroll container,
 * then the MUI table container fallback.
 * Returns null (no-op for callers) when none are present—e.g. window-level
 * desktop scroll or mobile card layout without `containedScroll`.
 *
 * @param root - Table root element that owns the scrollable region
 * @returns The scrollable HTMLElement, or null if none is found
 */
export const getTableScrollTarget = (root: HTMLElement): HTMLElement | null => {
  return (
    root.querySelector<HTMLElement>(MOBILE_SCROLL_BODY_SELECTOR) ??
    root.querySelector<HTMLElement>(TABLE_CONTAINER_SCROLL_SELECTOR) ??
    root.querySelector<HTMLElement>(MUI_TABLE_CONTAINER_SELECTOR)
  );
};

/**
 * Builds a stable key for the visible result set so scroll resets on pagination
 * and on search/filter refreshes that leave `pageIndex` at 0.
 * Uses JSON encoding so `|` (or other characters) inside `leadRowId` /
 * `contextKey` cannot collide with a delimiter.
 *
 * @param pageIndex - Current MRT pagination page index
 * @param rowCount - Total row count (server or client)
 * @param leadRowId - Id of the first row in the current `data` page
 * @param contextKey - Optional consumer search/filter (or similar) identity
 */
export const getTableResultSetKey = (
  pageIndex: number,
  rowCount: number,
  leadRowId: string,
  contextKey = ''
): string => JSON.stringify([pageIndex, rowCount, leadRowId, contextKey]);

/**
 * Scrolls the table list container to the top when the result set key changes
 * (pageIndex, rowCount, or lead row). Skips when the key is unchanged so
 * StrictMode remounts and ref-identity churn do not scroll. Uses useLayoutEffect
 * so the reset runs before paint and avoids a visible jump. No-ops when
 * `scrollTo` is unavailable (e.g. jsdom).
 *
 * @param tableRef - Ref attached to the table root (desktop list or mobile widget)
 * @param resultSetKey - Key from {@link getTableResultSetKey}
 */
export const useScrollTableToTopOnResultSetChange = (
  tableRef: RefObject<HTMLElement | null>,
  resultSetKey: string
): void => {
  const previousResultSetKeyRef = useRef(resultSetKey);

  useLayoutEffect(() => {
    if (previousResultSetKeyRef.current === resultSetKey) {
      return;
    }

    previousResultSetKeyRef.current = resultSetKey;

    const root = tableRef.current;
    if (!root) {
      return;
    }

    // jsdom (and some older environments) may lack Element.scrollTo.
    getTableScrollTarget(root)?.scrollTo?.({ top: 0, behavior: 'auto' });
  }, [resultSetKey, tableRef]);
};
